<template>
	<InventarisationPageTemplate v-slot="{ selectedIndex, customers }">
		<ExtractionSuggestionBanner
			v-if="extractionEnabled && matchedIncomeSuggestions.length > 0"
			:suggestions="matchedIncomeSuggestions"
			label="Geëxtraheerde inkomensgegevens beschikbaar"
			action-label="Bekijken en toepassen"
			@dismiss="extractionSuggestions.dismiss"
			@open="openSuggestion"
		/>

		<p
			v-if="extractionEnabled && unmatchedIncomeCount > 0"
			class="text-text-secondary mb-4"
			data-testid="unmatched-income-count"
		>
			Extracties zonder bepaalde klant: {{ unmatchedIncomeCount }}
		</p>
		<ExtractionApplyModal
			v-if="selectedSuggestion && modalContext"
			:key="`${modalContext.dossierId}:${modalContext.customerId}:${selectedSuggestion.id}`"
			:suggestion="selectedSuggestion"
			:dossier-id="modalContext.dossierId"
			:customer-id="modalContext.customerId"
			:version-data="dossierDataStore.data as Record<string, unknown> | null"
			:income-entries="loondienstEntries"
			@close="closeSuggestion"
			@applied="extractionSuggestions.fetchSuggestions"
			@mark-bekeken="extractionSuggestions.markBekeken"
		/>

		<InventarisationAppendSectionMenu
			:sections
			class="mb-10"
			@append="appendHandler"
		/>

		<FormKit
			v-if="customers[selectedIndex] && customer"
			:key="customer.customerId"
			v-model="customer.sections"
			:config
			type="form"
			:actions="false"
		>
			<div class="max-w-2xl pb-32">
				<InventarisationRepeatableSection
					v-for="section in sections"
					:key="section.type"
					:section="section"
					@remove="(id) => removeHandler(section.type as IncomeSectionKeys, id)"
					@insert="() => appendHandler(section.type)"
				/>

				<DashboardCardCollapsible v-if="SHOW_DEBUG" heading="DATA DEBUG">
					<pre><code>{{ JSON.stringify(customer.sections, null, 2) }}</code></pre>
				</DashboardCardCollapsible>
			</div>
		</FormKit>
	</InventarisationPageTemplate>
</template>

<script lang="ts" setup>
import { rootClasses } from "../../../formkit.theme";
import type { CustomerIncome, IncomeSectionKeys } from "@loanlink-nl/model-app";
import { computed, nextTick, ref, watch } from "vue";
import { useInventarisationStore } from "@/store/useInventarisationStore";
import { useDossierStore } from "@/store/useDossierStore";
import { useDossierDataStore } from "@/store/useDossierDataStore";
import { storeToRefs } from "pinia";
import { useFeatureFlag } from "@/composables/useFeatureFlag";
import {
	useExtractionSuggestions,
	type ExtractionSuggestion,
} from "@/composables/useExtractionSuggestions";
import ExtractionSuggestionBanner from "@/components/inventarisation/ExtractionSuggestionBanner.vue";
import ExtractionApplyModal from "@/components/inventarisation/ExtractionApplyModal.vue";
import InventarisationIncomeLoondienstForm from "@/components/inventarisation/income/InventarisationIncomeLoondienstForm.vue";
import InventarisationIncomeOverigForm from "@/components/inventarisation/income/InventarisationIncomeOverigForm.vue";
import InventarisationIncomePensioenForm from "@/components/inventarisation/income/InventarisationIncomePensioenForm.vue";
import InventarisationIncomeOndernemingForm from "@/components/inventarisation/income/InventarisationIncomeOndernemingForm.vue";
import InventarisationIncomeIvoForm from "@/components/inventarisation/income/InventarisationIncomeIvoForm.vue";
import InventarisationIncomeUitkeringForm from "@/components/inventarisation/income/InventarisationIncomeUitkeringForm.vue";

const inventarisationStore = useInventarisationStore();
const { selectedCustomerId: storeSelectedCustomerId } =
	storeToRefs(inventarisationStore);

const SHOW_DEBUG = import.meta.env.DEV;

const {
	customerHasEntry,
	addCustomerEntry,
	getCustomerEntry,
	appendSection,
	removeSection,
	getSectionIndex,
	removeInvalidValues,
} = inventarisationStore.income;

// Ensure customer entry exists when selection changes
watch(
	storeSelectedCustomerId,
	(newSelectedCustomerId) => {
		if (
			newSelectedCustomerId !== undefined &&
			!customerHasEntry(newSelectedCustomerId)
		) {
			addCustomerEntry(newSelectedCustomerId);
		}
		if (newSelectedCustomerId !== undefined) {
			void nextTick(() => {
				removeInvalidValues(newSelectedCustomerId);
			});
		}
	},
	{ immediate: true },
);

// Reactive customer entry - returns the actual reactive object from the store
// Mutations to customer.sections will propagate back to the source
const customer = computed<CustomerIncome | undefined>(() => {
	if (storeSelectedCustomerId.value === undefined) {
		return undefined;
	}
	return getCustomerEntry(storeSelectedCustomerId.value).value;
});

const removeHandler = (section: string, id: string) => {
	if (storeSelectedCustomerId.value) {
		removeSection(
			storeSelectedCustomerId.value,
			section as IncomeSectionKeys,
			id,
		);
	}
};

const appendHandler = (section: string) => {
	if (storeSelectedCustomerId.value) {
		appendSection(storeSelectedCustomerId.value, section as IncomeSectionKeys);

		if (section === "loondienst") {
			const entry = customer.value?.sections.loondienst?.[0];
			if (entry) {
				entry.values ??= {};
				entry.values.countryEmployer = "NL Nederland";
				entry.values.salaryPeriod = "jaar";
			}
		}
	}
};

const getDefaultHeadingFunction =
	(section: IncomeSectionKeys, label: string) => (id: string) => {
		if (storeSelectedCustomerId.value === undefined) {
			return label;
		}
		const sectionIndex = getSectionIndex(
			storeSelectedCustomerId.value,
			section,
			id,
		);
		return `${label} #${sectionIndex + 1}`;
	};

const sections = [
	{
		type: "loondienst",
		appendLabel: "Loondienst",
		component: InventarisationIncomeLoondienstForm,
		getHeading: getDefaultHeadingFunction("loondienst", "Loondienst"),
	},
	{
		type: "onderneming",
		appendLabel: "Onderneming",
		component: InventarisationIncomeOndernemingForm,
		getHeading: getDefaultHeadingFunction("onderneming", "Onderneming"),
	},
	{
		type: "ivo",
		appendLabel: "Inkomensverklaring ondernemer (IVO)",
		component: InventarisationIncomeIvoForm,
		getHeading: getDefaultHeadingFunction("ivo", "IVO"),
	},
	{
		type: "pensioen",
		appendLabel: "Pensioen",
		component: InventarisationIncomePensioenForm,
		getHeading: getDefaultHeadingFunction("pensioen", "Pensioen"),
	},
	{
		type: "uitkering",
		appendLabel: "Uitkering",
		component: InventarisationIncomeUitkeringForm,
		getHeading: getDefaultHeadingFunction("uitkering", "Uitkering"),
	},
	{
		type: "overig",
		appendLabel: "Overig",
		component: InventarisationIncomeOverigForm,
		getHeading: getDefaultHeadingFunction("overig", "Overig"),
	},
];

const dossierStore = useDossierStore();
const dossierDataStore = useDossierDataStore();
const extractionEnabled = useFeatureFlag("document-extraction-intermediary");
const selectedSuggestion = ref<ExtractionSuggestion>();

const modalContext = ref<{ dossierId: string; customerId: string }>();
function closeSuggestion() {
	selectedSuggestion.value = undefined;
	modalContext.value = undefined;
}
function openSuggestion(suggestion: ExtractionSuggestion) {
	const dossierId = dossierStore.selectedDossier?.id;
	const customerId = storeSelectedCustomerId.value;
	if (!dossierId || !customerId || suggestion.matchedCustomerId !== customerId)
		return;
	modalContext.value = { dossierId, customerId };
	selectedSuggestion.value = suggestion;
}
watch(
	[
		() => dossierStore.selectedDossier?.id,
		storeSelectedCustomerId,
		extractionEnabled,
	],
	closeSuggestion,
	{ flush: "sync" },
);

const loondienstEntries = computed(() => {
	const entries = customer.value?.sections.loondienst ?? [];
	return entries.map((entry, i) => ({
		id: entry.id ?? `loondienst-${i}`,
		label: (entry.values?.employerName as string) || `Loondienst #${i + 1}`,
	}));
});
const extractionSuggestions = useExtractionSuggestions(
	() => dossierStore.selectedDossier?.id,
);

const matchedIncomeSuggestions = computed(() => {
	const matched = extractionSuggestions.forCustomerAndSection(
		storeSelectedCustomerId.value,
		"income",
	).value;
	return matched;
});

const unmatchedIncomeCount = computed(
	() =>
		extractionSuggestions
			.unmatchedForSection("income")
			.value.filter((s) => s.status !== "toegepast").length,
);

const config = {
	rootClasses,
};
</script>
